import { Grid, MenuItem, Pagination, Stack, Typography, styled, useMediaQuery, useTheme } from '@mui/material';
import type { TypographyProps } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Loader from '../../Common/_components/Loader';
import { GetPatternCategories, GetPatterns, SelectCategory } from '../_actions/PatternDesignActions';
import { PatternsPerPage } from '../_stores/PatternDesignServiceStore';
import type PatternStore from '../_stores/PatternStore';
import PatternBrowserItem from './PatternBrowserItem';
import SearchBar from '../../Common/ItemList/SearchBar';
import SelectV2 from '../../Common/Form/SelectV2';
import { useAppDispatch } from '../../Common/_hooks/useAppDispatch';
import Dialog from '../../Common/Dialog/Dialog';
import DialogContent from '../../Common/Dialog/DialogContent';
import DialogPaper from '../../Common/Dialog/DialogPaper';
import { useAppSelector } from '../../Common/_hooks/useAppSelector';


const StyledDialogPaper = styled(DialogPaper)(({ theme }) => ({
  height: '90%',

  '&.MuiDialog-paperFullScreen': {
    height: '100%',
  },

  [theme.breakpoints.down('dialog_mobile')]: {
    borderRadius: 0,
    height: '100%',
    margin: 0,
    maxHeight: 'none',
    width: '100%',
  }
}))

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  boxSizing: 'border-box',
  height: '100%',
  overflow: 'hidden',
  padding: theme.spacing(3, 3, 0, 3),
  marginTop: 0,

  [theme.breakpoints.down('md')]: {
    height: 'auto',
    overflowX: 'hidden',
    overflowY: 'auto',
    padding: theme.spacing(2),
  }
}))

const PatternBrowserLayout = styled(Stack)(({ theme }) => ({
  height: '100%',
  minHeight: 0,

  [theme.breakpoints.down('md')]: {
    height: 'auto',
  },
}))

const PatternBrowserSidebar = styled('div')(({ theme }) => ({
  minWidth: 0,
  width: 'auto',

  [theme.breakpoints.up('md')]: {
    width: 300,
    flexShrink: 0,
    boxSizing: 'border-box',
    height: '100%',
    minHeight: 0,
    overflowX: 'hidden',
    overflowY: 'auto',
    scrollbarGutter: 'stable',
    paddingBottom: theme.spacing(3),
    paddingRight: theme.spacing(1),

    '&::-webkit-scrollbar': {
      width: '10px',
      height: '10px',
    },

    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
    },

    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'rgba(0, 0, 0, 0.15)',
      borderRadius: '10px',

      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.25)',
      }
    }
  },
}))

const PatternBrowserResults = styled('div')(({ theme }) => ({
  minWidth: 0,

  [theme.breakpoints.up('md')]: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
  },

  [theme.breakpoints.down('md')]: {
    marginTop: theme.spacing(3),
  }
}))

const PatternResultsScroller = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    boxSizing: 'border-box',
    flex: '1 1 auto',
    minHeight: 0,
    overflowX: 'hidden',
    overflowY: 'auto',
    paddingBottom: theme.spacing(3),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(3),
    scrollbarGutter: 'stable',

    '&::-webkit-scrollbar': {
      width: '10px',
      height: '10px',
    },

    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
    },

    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'rgba(0, 0, 0, 0.15)',
      borderRadius: '10px',

      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.25)',
      }
    }
  },
}))

const PatternResultsGrid = styled(Grid)({
  alignContent: 'flex-start',
  boxSizing: 'border-box',
  width: '100%',
})

const ResultsSearch = styled('div')({
  '& > div': {
    marginBottom: 0,
  },
})

const CategoryList = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  paddingBottom: theme.spacing(3),
}))

const SubCategoryList = styled('div')({
  display: 'flex',
  flexDirection: 'column',
})

const CategoryButton = styled((props: TypographyProps) => (
  <Typography variant="h4" {...props} />
))({
  display: 'block',
  textAlign: 'left',
  width: '100%',
  fontSize: '18px',
  padding: '5px 0',
  cursor: 'pointer',

  '&.active': {
    color: '#ea008a',
  },

  '&:hover': {
    color: '#ea008a',
  },
})

const MobileCategory = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(1),

  [theme.breakpoints.up('md')]: {
    display: 'none',
  }
}))

const DesktopCategory = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',

  [theme.breakpoints.down('md')]: {
    display: 'none',
  }
}))

const StyledPagination = styled(Pagination)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  padding: theme.spacing(2, 0),

  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(1, 0),
  }
}))


export interface Props {
  opened: boolean
  onClose: () => void
  onSelect: (item: PatternStore) => void
}

export default function PatternBrowser(props: Props) {
  const dispatch = useAppDispatch();
  const [t] = useTranslation('design-lab');
  const theme = useTheme()
  const breakpointSmDown = useMediaQuery(theme.breakpoints.down('sm'))

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const loading = useAppSelector(state => state.get('UIData').get('loaders').get('images'));
  const listCategories = useAppSelector(state => state.get('UIData').get('designLab').get('patternDesignService').get('categories'));
  const listPatterns = useAppSelector(state => state.get('UIData').get('designLab').get('patternDesignService').get('patterns'));
  const selectedCategoryId = useAppSelector(state => state.get('UIData').get('designLab').get('patternDesignService').get('selectedCategoryId'));
  const count = useAppSelector(state => state.get('UIData').get('designLab').get('patternDesignService').get('count'));
  const [openedCategory, setOpenedCategory] = useState<number | 'all'>('all');

  const listSubcategories = useMemo(() => {
    return openedCategory !== 'all' ? listCategories.get(openedCategory)?.get('categories') : undefined
  }, [listCategories, openedCategory]);

  useEffect(() => {
    dispatch(GetPatternCategories());
  }, []);

  useEffect(() => {
    dispatch(GetPatterns(selectedCategoryId ?? null, search, PatternsPerPage, (page - 1) * PatternsPerPage));
  }, [selectedCategoryId, search, page]);

  const renderItem = useCallback((pattern: PatternStore) => {
    return <PatternBrowserItem
      key={pattern.get('id')}
      item={pattern}
      onSelect={props.onSelect}
      onClose={props.onClose}
    />
  }, [props.onSelect, props.onClose])

  return <>
    <Dialog
      open={props.opened}
      onClose={props.onClose}
      fullWidth={true}
      maxWidth="xl"
      PaperComponent={StyledDialogPaper}
      showCloseButton
      fullScreenOnMobile="md"
      closeButtonSx={{
        backgroundColor: 'common.white',

        '&:hover': {
          backgroundColor: 'common.white',
        }
      }}
    >
      <StyledDialogContent>
        <PatternBrowserLayout
          direction={{
            xs: 'column',
            md: 'row',
          }}
          spacing={1}
        >
          <PatternBrowserSidebar
          >
            <Typography
              variant="h2"
              style={{
                marginBottom: '20px',
              }}
            >{t('Pattern Library')}</Typography>

            <Typography
              variant="body2"
              style={{
                marginBottom: '20px',
              }}
            >{t('All patterns in this library are from patterndesigns.com. Products created using patterns from this library will have an additional 12\u0025 fee when ordered.')}</Typography>

            <MobileCategory>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <SelectV2
                    value={openedCategory ?? 'all'}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      const categoryId = e.target.value !== 'all' ? Number(e.target.value) : null;
                      setPage(1);
                      dispatch(SelectCategory(categoryId));
                      setOpenedCategory(categoryId ?? 'all');
                    }}
                  >
                    <MenuItem value="all">{t('All Categories')}</MenuItem>
                    {listCategories.valueSeq().map(category => {
                      return <MenuItem
                        key={category.get('id')}
                        value={category.get('id')}
                      >{category.get('title')}</MenuItem>
                    })}
                  </SelectV2>
                </Grid>
                <Grid item xs={6}>
                  {openedCategory !== null && listSubcategories !== undefined ? <SelectV2
                    value={selectedCategoryId !== null && listSubcategories.has(selectedCategoryId) ? selectedCategoryId : openedCategory}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      setPage(1);
                      dispatch(SelectCategory(Number(e.target.value)));
                    }}
                  >
                    <MenuItem value={openedCategory}>{t('All Sub-Categories')}</MenuItem>
                    {listSubcategories.valueSeq().map(subCategory => {
                      return <MenuItem
                        key={subCategory.get('id')}
                        value={subCategory.get('id')}
                      >{subCategory.get('title')}</MenuItem>
                    })}
                  </SelectV2> : null}
                </Grid>
              </Grid>
            </MobileCategory>

            <DesktopCategory>
              <Typography
                variant="h3"
                style={{
                  marginBottom: '10px',
                }}
              >{t('Categories:')}</Typography>
              <CategoryList>
                <CategoryButton
                  className={openedCategory === null && selectedCategoryId === null ? 'active' : ''}
                  onClick={() => {
                    setPage(1);
                    dispatch(SelectCategory(null));
                    setOpenedCategory('all');
                  }}
                >{t('All')}</CategoryButton>

                {listCategories.valueSeq().map(category => {
                  return <div key={category.get('id')}>
                    <CategoryButton
                      className={openedCategory === category.get('id') || selectedCategoryId === category.get('id') ? 'active' : ''}
                      onClick={() => {
                        setPage(1);
                        dispatch(SelectCategory(category.get('id')))
                        setOpenedCategory(openedCategory !== category.get('id') ? category.get('id') : 'all');
                      }}
                    >{category.get('title')}</CategoryButton>
                    {openedCategory === category.get('id') && <SubCategoryList style={{
                      paddingLeft: '20px',
                    }}>
                      {category.get('categories').valueSeq().map(subCategory => {
                        return <CategoryButton
                          key={subCategory.get('id')}
                          className={selectedCategoryId === subCategory.get('id') ? 'active' : ''}
                          style={{
                            fontSize: '16px',
                          }}
                          onClick={() => {
                            setPage(1);
                            dispatch(SelectCategory(subCategory.get('id')))
                          }}
                        >{subCategory.get('title')}</CategoryButton>
                      })}
                    </SubCategoryList>}
                  </div>
                })}
              </CategoryList>
            </DesktopCategory>
          </PatternBrowserSidebar>
          <PatternBrowserResults>
            <PatternResultsScroller>
              <PatternResultsGrid container spacing={{ xs: 1, sm: 2 }}>
                <Grid item xs={12}>
                  <ResultsSearch>
                    <SearchBar
                      placeholder={t('Search')}
                      value={search}
                      onChange={(value) => {
                        setSearch(value);
                        setPage(1);
                      }}
                    ></SearchBar>
                  </ResultsSearch>
                </Grid>
                {listPatterns.valueSeq().map(renderItem)}
              </PatternResultsGrid>
            </PatternResultsScroller>
            <StyledPagination
              count={Math.ceil(count / PatternsPerPage)}
              showFirstButton={breakpointSmDown ? false : Math.ceil(count / PatternsPerPage) > 5}
              showLastButton={breakpointSmDown ? false : Math.ceil(count / PatternsPerPage) > 5}
              siblingCount={breakpointSmDown ? 0 : 1}
              page={page}
              onChange={(e, value) => {
                setPage(value);
              }}
            />
          </PatternBrowserResults>
        </PatternBrowserLayout>
      </StyledDialogContent>
      {loading && <Loader noModal />}
    </Dialog>
  </>
}
